import { Component, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Observable, Subject } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { BookManagementService } from '../../book-management/book-management.service';
import { AdminService } from '../../admin/admin.service';
import { MatDialog } from '@angular/material/dialog';
import { RbGridComponent } from '../../shared-components/rb-grid/rb-grid.component';
import { SessionStorageService } from '../../services/session-storage.service';
import { saveAs } from '@progress/kendo-file-saver';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DateTime } from 'luxon';
import { GridResults } from '../../models/general-models';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';

@Component({
  selector: 'app-carrier-policy-details',
  templateUrl: './carrier-policy-details.component.html',
  styleUrls: ['./carrier-policy-details.component.scss'],
  imports: [
    RbGridComponent,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
  ],
  providers: [provideLuxonDateAdapter()],
})
export class CarrierPolicyDetailsComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  adminService = inject(AdminService);
  bmSvc = inject(BookManagementService);
  private dialog = inject(MatDialog);
  private location = inject(Location);
  private route = inject(Router);
  private snacky = inject(MatSnackBar);
  private ss = inject(SessionStorageService);

  @ViewChild(RbGridComponent) rbGrid: RbGridComponent;

  unsubscribe: Subject<any> = new Subject();
  policyId = '';
  navCollapsed = false;
  crumbConfig = [];
  gridConfig = {
    title: 'Policy Details',
    gridName: 'CarrierPolicyDetails',
    showGridFilters: true,
    showFilterManager: true,
    manualSearch: true,
    searchButtonText: 'Search Policies',
    showBulkActions: false,
    downloadGridButton: false,
    gridActionColumn: false,
    selectableRows: false,
    gridType: 'book',
    detailViewRoute: '/book-management/',
    showBridgeIdSearch: true,
    showGridColumnFilters: true,
    reorderable: true,
  };
  current = 'pm';
  homeOfficeUser = false;
  hasRepCodes = false;
  launchButton;
  fileLoading = false;

  ngOnInit(): void {
    this.bmSvc.launchButton$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(buttonVals => {
        if (!buttonVals['ShowLaunchButton']) {
          this.gridConfig.selectableRows = false;
        }
        this.launchButton = buttonVals;
      });
    this.navCollapsed = this.ss.get('utilityToolbarCollapsed');
    this.current = this.location.path().split('/')[2] || 'pm';
    const globals = this.ss.get('globals');
    const repCodes = this.ss.get('repUnits');

    if (globals.user.managerUnitCount > 0) {
      this.homeOfficeUser = true;
    } else if (globals.user.managerUnitCount === 0) {
      this.homeOfficeUser = false;
    }

    if (repCodes && repCodes.length > 0) {
      this.hasRepCodes = true;
    }

    if (this.current === 'office-view') {
      this.crumbConfig.push({
        label: 'Office Reports',
        link: '/office-view',
      });
    } else if (this.current === 'rep-reports') {
      this.crumbConfig.push({
        label: 'Rep Reports',
        link: '/rep-reports',
      });
    } else if (this.current === 'pm') {
      this.crumbConfig.push({
        label: 'Book Management',
        link: '/book-management',
      });
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  nav(location) {
    this.route.navigate([`/${location}`]);
  }

  viewPolicy(ev, dataItem, context) {
    if (ev) {
      ev.preventDefault();
      ev.stopPropagation();
    }

    window.open(
      `${context.location.prepareExternalUrl('')}book-management/${
        dataItem.ID
      }`,
      '_blank'
    );
  }

  getGridSetup(): Observable<GridResults> {
    const defaultview = {
      filter: [
        {
          IssueDate: {
            start: DateTime.now()
              .minus({ years: 1 })
              .startOf('month')
              .toUTC()
              .toISO(),
            end: DateTime.now().toUTC().toISO(),
          },
        },
      ],
      sort: [],
    };

    const getHeaders = this.bmSvc.gridSetup(true);

    return forkJoin([getHeaders]).pipe(
      map(([data1]) => ({ ...data1, defaultview }))
    );
  }

  getGridData(reqData) {
    return this.bmSvc.policyManagementData(reqData, true);
  }

  downloadPolicyList() {
    this.fileLoading = true;
    this.snacky.open(`File downloading. Please wait.`, 'Close', {
      duration: 4000,
    });
    const queryData = this.rbGrid.generateQueryData(null, {
      take: 100000,
      skip: 0,
    });
    queryData['excludedColumns'] = this.rbGrid.hidden;

    this.bmSvc.downloadPolicyManagementData(queryData, true).subscribe({
      next: resp => {
        if (resp) {
          let fileData;
          const filename = `AnnuityBookManagement.csv`;
          const reader = new FileReader();
          reader.readAsDataURL(resp);

          reader.onloadend = () => {
            this.fileLoading = false;
            fileData = reader.result;
            saveAs(fileData, filename);
            this.snacky.open(`Download complete.`, 'Close', {
              duration: 4000,
            });
          };
        } else {
          this.fileLoading = false;
          this.snacky.open(
            `Oops! Something went wrong, please try again later.`,
            'Close',
            {
              duration: 4000,
            }
          );
        }
      },
      error: err => {
        console.warn('Error: ', err);
        this.fileLoading = false;
        this.snacky.open(
          `Oops! Something went wrong, please try again later.`,
          'Close',
          {
            duration: 4000,
          }
        );
      },
    });
  }

  toggleNavCollapse() {
    this.navCollapsed = !this.navCollapsed;
  }

  disableLaunchTo() {
    return this.rbGrid.selectedTradeList.length <= 0;
  }
}
